import Vue from 'vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import i18n from '@/libs/i18n'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'
import { invalidateSubscriptionStanding } from '@/libs/subscriptionStanding'
import {
  presentSubscriptionActionOutcome,
  SubscriptionActionOutcome,
} from '@/libs/subscriptionActionGuard'
import {
  CredentialStateError,
  authorityIdFor,
  compareAuthority,
  compareCredential,
  randomBase64Url,
  readCredentialState,
  sameAuthorityId,
  withCredentialStateFence,
} from '@/auth/credentialState'
import jwtDefaultConfig from './jwtDefaultConfig'

const ACCOUNT_USER = 'user'
const ACCOUNT_PARENT = 'parent'
const AUTH_CHANNEL = 'diko-auth'
const CREDENTIAL_FLOOR_HEADER = 'X-Diko-Credential-Floor'
const TRANSPORT_TIMEOUT_MS = 30000
const MAX_REFRESH_INSTALL_ATTEMPTS = 16
const FACTOR_RECOVERY_WINDOW_MS = 10 * 60 * 1000
const FACTOR_READY_TTL_MS = 30 * 1000
const PHONE_SETUP_CONTINUATION_GRACE_MS = 30000
const SESSION_REPLACED = 'SESSION_REPLACED'
const SESSION_EXPIRED = 'SESSION_EXPIRED'

// CODE-R4-004: a distinguishable internal signal, not a CredentialStateError
// -- a same-authority success racing a pending factor operation is not
// invalid, it is not-yet-decidable, so it must be caught and rerouted to the
// stale-success parking flow rather than surfaced to the caller as a
// rejection.
const FACTOR_PENDING_PARK = 'FACTOR_PENDING_PARK'

const factorPendingParkError = () => {
  const error = new Error('Credential response is parked behind a pending factor operation')
  error.code = FACTOR_PENDING_PARK
  return error
}

const isBrowser = () => typeof window !== 'undefined'

const dispatchAuthEvent = (name, detail) => {
  if (!isBrowser() || typeof window.dispatchEvent !== 'function') return
  window.dispatchEvent(new CustomEvent(name, { detail }))
}

const decodeTokenPayload = token => {
  if (!token || typeof token !== 'string') return 0
  try {
    const part = token.split('.')[1]
    const payload = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')
    return JSON.parse(window.atob(payload))
  } catch (error) {
    return null
  }
}

const decodeExpiry = token => {
  const payload = decodeTokenPayload(token)
  return payload && payload.exp ? payload.exp * 1000 : 0
}

const normalizeExpiry = value => {
  if (!value) return 0
  if (typeof value === 'number') return value < 100000000000 ? value * 1000 : value
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

const responseCode = error => {
  const data = error && error.response && error.response.data
  return data && (data.code || data.errorCode)
}

// The server's own definitive answer that a session no longer exists: it
// replies 401 with the numeric errorCode plus the matching string `code`
// (server: authSession.js#SessionAuthError), and responseCode reads the string
// first, so both shapes have to be compared here.
const DEAD_SESSION_CODES = ['401032', '401033', SESSION_REPLACED, SESSION_EXPIRED]

const isDeadSessionError = error => {
  const status = error && error.response && error.response.status
  return status === 401 && DEAD_SESSION_CODES.includes(String(responseCode(error)))
}

const safeVersion = value => Number.isSafeInteger(value) && value >= 0

const normalizeCredential = value => {
  if (!value || typeof value !== 'object') return null
  if (!safeVersion(value.ae) || !safeVersion(value.se) || !safeVersion(value.rv)) return null
  const tuple = {
    userId: String(value.userId || ''),
    sid: String(value.sid || ''),
    ae: value.ae,
    se: value.se,
    rv: value.rv,
  }
  if (!tuple.userId || !tuple.sid) return null
  return tuple
}

const authorityFor = tuple => ({
  userId: tuple.userId,
  sid: tuple.sid,
  ae: tuple.ae,
  se: tuple.se,
})

const tupleFromAccessToken = token => {
  const payload = decodeTokenPayload(token)
  return normalizeCredential(payload && {
    userId: payload.userId,
    sid: payload.sid,
    ae: payload.ae,
    se: payload.se,
    rv: payload.rv,
  })
}

const credentialsEqual = (left, right) => Boolean(
  left && right
  && sameAuthorityId(left, right)
  && left.ae === right.ae
  && left.se === right.se
  && left.rv === right.rv,
)

const tombstoneBlocks = (state, authorityId, tuple) => {
  const raw = state.tombstones && state.tombstones[authorityId]
  if (!raw) return false
  const tombstone = normalizeCredential(raw)
  if (!tombstone || authorityIdFor(tombstone) !== authorityId) return true
  return !tuple || compareCredential(tuple, tombstone) <= 0
}

const base64UrlJson = value => {
  const json = JSON.stringify(value)
  const encoded = window.btoa(json).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
  if (encoded.length > 512) throw new CredentialStateError('Credential floor is too large')
  return encoded
}

const randomUuid = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  // eslint-disable-next-line no-bitwise
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  // eslint-disable-next-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const sleep = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))

// CODE-R4-004: 'restart-required' (503003) means the server-side write may
// already have committed and is only waiting on /auth/factor/recover to
// prove it -- it is not a precommit abort, so a waiter must keep treating it
// like 'pending' up to the same deadline, exactly like the operation
// owner's own recovery attempt would. Only 'ready' (proven success) exits
// this set on the success side; 'terminal'/'retryable'/'failed' are settled
// dispositions the caller must reason about explicitly (see
// parkBehindFactorOperation and recoverCredentialAuthority).
const FACTOR_NOT_YET_DECIDED_STATES = ['pending', 'restart-required']

// Shared by CODE-R1-013 sibling recovery and CODE-R4-004 stale-success
// parking: polls the journal (works for both BroadcastChannel and the
// storage-event transport, since both ultimately just mutate localStorage)
// until the named factor operation leaves a not-yet-decided state or the
// deadline passes.
const waitForFactorSettled = async (operationId, deadline) => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let factor
    try {
      ({ factor } = readCredentialState().state)
    } catch (error) {
      return null
    }
    if (!factor
      || factor.operationId !== operationId
      || !FACTOR_NOT_YET_DECIDED_STATES.includes(factor.state)
      || Date.now() >= deadline) {
      return factor
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(100)
  }
}

const factorOutcomeError = (status, errorCode, message) => {
  const error = new Error(message)
  error.response = { status, data: { status, errorCode, message } }
  return error
}

const hasExactFactorTerminalEvidence = (error, operation, expectedEndpoint) => {
  if (!operation || !expectedEndpoint) return false
  if (!error || !error.response || error.response.status !== 400) return false
  if (Number(responseCode(error)) !== 400018) return false

  const config = error.config || {}
  if (config.url !== expectedEndpoint) return false
  let { data: requestData } = config
  requestData = requestData || {}
  if (typeof requestData === 'string') {
    try {
      requestData = JSON.parse(requestData)
    } catch (parseError) {
      return false
    }
  }
  if (!requestData || String(requestData.operationId) !== String(operation.operationId)) return false
  if (String(requestData.challengeId) !== String(operation.challengeId)) return false

  try {
    const { factor } = readCredentialState().state
    return Boolean(factor)
      && factor.state === 'pending'
      && factor.operationId === operation.operationId
      && Number(factor.fence) === Number(operation.fence)
      && factor.authorityId === operation.authorityId
  } catch (readError) {
    return false
  }
}

const isFactorReadyExpired = factor => {
  if (!factor || !factor.expiresAt) return false
  return factor.state === 'ready' && factor.expiresAt <= Date.now()
}

export default class JwtService {
  axiosIns = null;

  jwtConfig = { ...jwtDefaultConfig };

  accessToken = null;

  accessTokenExpiresAt = 0;

  accountType = null;

  session = null;

  refreshPromise = null;

  bootstrapPromise = null;

  refreshTimer = null;

  channel = null;

  tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  credentialSlot = null;

  factorOperation = null;

  factorRecoveryPromise = null;

  tokenReissuedListeners = new Set();

  currentLoginIntent = null;

  currentLoginGrant = null;

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }
    this.axiosIns.defaults.withCredentials = true

    this.migrateLegacyParentSession()
    this.removeLegacyRefreshTokens()
    this.setupCrossTabCoordination()
    this.setupRecoveryListeners()

    this.axiosIns.interceptors.request.use(
      config => {
        const token = this.getToken()
        const headers = config.headers || {}
        const hasAuthorization = Boolean(headers.Authorization || headers.authorization)
        if (token && !config.skipAuthToken && !hasAuthorization) {
          // eslint-disable-next-line no-param-reassign
          config.headers = headers
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${token}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    this.axiosIns.interceptors.response.use(
      response => response,
      error => this.handleResponseError(error),
    )
  }

  migrateLegacyParentSession() {
    if (!isBrowser()) return
    const marker = localStorage.getItem(this.jwtConfig.parentAuthMarkerKeyName)
    const legacyToken = localStorage.getItem(this.jwtConfig.storageTokenKeyName)

    if (!marker && legacyToken) {
      let cachedAbility = []
      try {
        const cached = JSON.parse(localStorage.getItem('userData'))
        cachedAbility = cached && Array.isArray(cached.ability) ? cached.ability : []
      } catch (error) {
        cachedAbility = []
      }
      const isParent = cachedAbility.some(rule => rule.action === 'actAs' && rule.subject === 'parent')
      if (isParent) localStorage.setItem(this.jwtConfig.parentAuthMarkerKeyName, '1')
    }

    if (localStorage.getItem(this.jwtConfig.parentAuthMarkerKeyName) && legacyToken) {
      this.accountType = ACCOUNT_PARENT
    } else {
      localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(this.jwtConfig.parentAuthMarkerKeyName)
    }
  }

  removeLegacyRefreshTokens() {
    if (!isBrowser()) return
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    sessionStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    sessionStorage.removeItem(this.jwtConfig.storageTokenKeyName)
  }

  setupCrossTabCoordination() {
    if (!isBrowser()) return
    const observe = () => this.observeCredentialState().catch(() => {
      this.clearPractitionerState()
      dispatchAuthEvent('diko:auth-required', { reason: SESSION_EXPIRED })
    })
    if (typeof window.BroadcastChannel === 'function') {
      this.channel = new window.BroadcastChannel(AUTH_CHANNEL)
      this.channel.onmessage = event => {
        const message = event.data || {}
        if (message.source === this.tabId) return
        if (message.type === 'credential-state-changed') observe()
      }
    }
    window.addEventListener('storage', event => {
      if (event.key && event.key.startsWith('diko-credential-state:v1:')) observe()
    })
  }

  setupRecoveryListeners() {
    if (!isBrowser()) return
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.recoverAuthentication()
    })
    window.addEventListener('online', () => this.recoverAuthentication())
  }

  recoverAuthentication() {
    if (this.accountType !== ACCOUNT_USER && !this.accessToken) return
    this.ensureFreshAccessToken(60).catch(error => {
      if (responseCode(error) === SESSION_REPLACED) this.handleSessionReplaced(error.response.data)
    })
  }

  broadcast(type, payload) {
    if (this.channel) this.channel.postMessage({ type, payload, source: this.tabId })
  }

  broadcastCredentialState(token) {
    this.broadcast('credential-state-changed', token ? {
      fence: token.fence,
      ownerNonce: token.ownerNonce,
    } : null)
  }

  isParentSession() {
    return this.accountType === ACCOUNT_PARENT
      && isBrowser()
      && Boolean(localStorage.getItem(this.jwtConfig.parentAuthMarkerKeyName))
      && Boolean(localStorage.getItem(this.jwtConfig.storageTokenKeyName))
  }

  isAuthenticated() {
    if (this.isParentSession()) return true
    return this.accountType === ACCOUNT_USER && this.isAccessTokenValid(0)
  }

  isAccessTokenValid(minValiditySeconds = 0) {
    if (!this.getToken()) return false
    if (!this.accessTokenExpiresAt) return true
    return this.accessTokenExpiresAt - Date.now() > minValiditySeconds * 1000
  }

  getToken() {
    if (this.accountType === ACCOUNT_PARENT && isBrowser()) {
      return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
    }
    if (this.credentialSlot) {
      try {
        const { state } = readCredentialState()
        const { accepted } = state
        if (!accepted
          || tombstoneBlocks(state, this.credentialSlot.authorityId, this.credentialSlot.C)
          || accepted.authorityId !== this.credentialSlot.authorityId
          || compareAuthority(this.credentialSlot.A, accepted.authorityFloor) < 0
          || state.credentialAuthorityRevision !== this.credentialSlot.credentialAuthorityRevision) {
          return null
        }
      } catch (error) {
        return null
      }
    }
    return this.accessToken
  }

  getRefreshToken() {
    this.removeLegacyRefreshTokens()
    return null
  }

  setToken(value, session = this.session, credentialSlot = null) {
    // A token install can be a login, logout, refresh, or identity switch.
    // The entitlement cache is deliberately shorter-lived than credentials
    // and must never cross any of those authority boundaries.
    invalidateSubscriptionStanding()
    if (!value) {
      this.accessToken = null
      this.accessTokenExpiresAt = 0
      this.credentialSlot = null
      return
    }
    this.accountType = ACCOUNT_USER
    this.accessToken = value
    this.session = session || null
    this.credentialSlot = credentialSlot
    this.accessTokenExpiresAt = normalizeExpiry(session && session.accessTokenExpiresAt) || decodeExpiry(value)
    if (isBrowser()) localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    this.scheduleProactiveRefresh()
  }

  setRefreshToken() {
    this.removeLegacyRefreshTokens()
  }

  scheduleProactiveRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    if (this.accountType !== ACCOUNT_USER || !this.accessTokenExpiresAt) return
    const delay = Math.max(0, this.accessTokenExpiresAt - Date.now() - 60000)
    this.refreshTimer = setTimeout(() => {
      this.ensureFreshAccessToken(60).catch(error => {
        if (responseCode(error) === SESSION_REPLACED) this.handleSessionReplaced(error.response.data)
        else dispatchAuthEvent('diko:auth-required', { reason: SESSION_EXPIRED })
      })
    }, delay)
  }

  captureInstallSnapshot() {
    const localKeys = [
      this.jwtConfig.storageTokenKeyName,
      this.jwtConfig.storageRefreshTokenKeyName,
      this.jwtConfig.parentAuthMarkerKeyName,
      'userData',
      'userName',
      'userAvatar',
    ]
    const sessionKeys = [this.jwtConfig.storageTokenKeyName, this.jwtConfig.storageRefreshTokenKeyName]
    const values = (storage, keys) => Object.fromEntries(keys.map(key => [key, storage.getItem(key)]))
    return {
      fields: {
        accountType: this.accountType,
        accessToken: this.accessToken,
        accessTokenExpiresAt: this.accessTokenExpiresAt,
        session: this.session,
        credentialSlot: this.credentialSlot,
        factorOperation: this.factorOperation,
      },
      abilityRules: Array.isArray(ability.rules)
        ? ability.rules.map(rule => ({ ...rule }))
        : null,
      localKeys,
      sessionKeys,
      localValues: isBrowser() ? values(localStorage, localKeys) : {},
      sessionValues: isBrowser() ? values(sessionStorage, sessionKeys) : {},
    }
  }

  installRollback(snapshot) {
    const values = (storage, keys) => Object.fromEntries(keys.map(key => [key, storage.getItem(key)]))
    const writtenLocal = isBrowser() ? values(localStorage, snapshot.localKeys) : {}
    const writtenSession = isBrowser() ? values(sessionStorage, snapshot.sessionKeys) : {}
    return () => {
      const restore = (storage, keys, before, written) => keys.forEach(key => {
        if (storage.getItem(key) !== written[key]) return
        if (before[key] === null) storage.removeItem(key)
        else storage.setItem(key, before[key])
      })
      if (isBrowser()) {
        restore(localStorage, snapshot.localKeys, snapshot.localValues, writtenLocal)
        restore(sessionStorage, snapshot.sessionKeys, snapshot.sessionValues, writtenSession)
      }
      if (this.refreshTimer) clearTimeout(this.refreshTimer)
      Object.assign(this, snapshot.fields)
      this.refreshTimer = null
      if (snapshot.abilityRules) ability.update(snapshot.abilityRules)
      this.scheduleProactiveRefresh()
    }
  }

  runReversibleInstall(mutate) {
    const snapshot = this.captureInstallSnapshot()
    try {
      mutate()
      return this.installRollback(snapshot)
    } catch (error) {
      this.installRollback(snapshot)()
      throw error
    }
  }

  cachePresentation(data) {
    if (!isBrowser() || !data) return
    const user = data.user || {}
    const cached = {
      ability: data.ability || [],
      subscriptionStatus: user.subscriptionStatus,
      trialEndsAt: user.trialEndsAt,
    }
    localStorage.setItem('userData', JSON.stringify(cached))
    const displayName = user.displayName || user.name
    if (displayName) localStorage.setItem('userName', displayName)
    if (user.avatar) localStorage.setItem('userAvatar', `${this.axiosIns.defaults.baseURL}${user.avatar}`)
    ability.update(data.ability || initialAbility)
  }

  // eslint-disable-next-line class-methods-use-this
  validateCredentialResponse(data) {
    if (!data || !data.accessToken) throw new CredentialStateError('Credential response is missing an access token')
    const declared = normalizeCredential(data.credentialVersion)
    const signed = tupleFromAccessToken(data.accessToken)
    if (!declared || !signed || !credentialsEqual(declared, signed)) {
      throw new CredentialStateError('Credential response does not match its access token')
    }
    return declared
  }

  async acceptCredentialResponse(data, context = {}) {
    if (!data || !data.accessToken) return data

    if (data.isParent) {
      // CODE-R1-010: route Parent installation through the same fenced,
      // ordered authority protocol as User credentials -- it shares
      // localStorage keys (parentAuthMarkerKeyName/storageTokenKeyName) and
      // presentation caching with the rest of this class, so it must be
      // mutually exclusive with a concurrent User install, not a bypass.
      const result = await withCredentialStateFence(state => {
        if (context.serverIntentIdentity && !this.intentMatchesState(context.serverIntentIdentity, state)) {
          throw new CredentialStateError('Parent response belongs to a superseded login intent')
        }
        // CODE-R1-010: represent Parent acceptance explicitly in the ordered
        // journal instead of returning the state unchanged -- tombstone
        // whatever User authority was previously accepted so a stale/
        // delayed User response for it can never be reaccepted later and
        // silently flip this (or a sibling) tab back out of Parent.
        const { accepted } = state
        return {
          ...state,
          credentialAuthorityRevision: state.credentialAuthorityRevision + 1,
          accepted: null,
          factor: null,
          tombstones: accepted
            ? { ...state.tombstones, [accepted.authorityId]: accepted.cookieWatermark }
            : state.tombstones,
        }
      }, () => ({
        commit: () => this.runReversibleInstall(() => {
          invalidateSubscriptionStanding()
          this.cachePresentation(data)
          this.accountType = ACCOUNT_PARENT
          this.accessToken = null
          this.accessTokenExpiresAt = 0
          this.session = null
          this.credentialSlot = null
          if (isBrowser()) {
            localStorage.setItem(this.jwtConfig.parentAuthMarkerKeyName, '1')
            localStorage.setItem(this.jwtConfig.storageTokenKeyName, data.accessToken)
          }
        }),
      }))
      if (!result.committed) throw new CredentialStateError('Parent response lost its installation fence')
      return data
    }

    const tuple = this.validateCredentialResponse(data)
    const authorityId = authorityIdFor(tuple)
    let result
    try {
      result = await withCredentialStateFence((state, fenceToken) => {
        const { accepted } = state
        const sameGroup = accepted && sameAuthorityId(accepted.cookieWatermark, tuple)
        const switching = accepted && !sameGroup
        const modernSwitch = context.serverIntentIdentity && this.intentMatchesState(context.serverIntentIdentity, state)
        const legacySwitch = context.legacyLane && context.legacyLane.laneHash
        // CODE-R3-004: an exact reported prior-commit watermark authorizes
        // switching to precisely that authority (never a different one, and
        // never below what the watermark itself reported) -- see
        // reconcilePriorLoginCommit, the only caller that sets this context.
        const watermarkSwitch = Boolean(context.priorWatermark)
          && sameAuthorityId(context.priorWatermark, tuple)
          && compareCredential(tuple, context.priorWatermark) >= 0
        // A registration response carries an authority the server minted
        // inside that same request, for a user that did not exist before it,
        // so there is no earlier intent for it to match. Without this grant a
        // stale `accepted` blocks it forever: clearAuthState leaves the
        // journal untouched, so any session that died without an explicit
        // logout keeps its authority accepted, and the very next registration
        // in that browser is created server-side and then refused here.
        // Scoped to the register() call site only -- it authorizes the switch
        // and nothing else; the tombstone gate below still applies in full.
        const registrationSwitch = Boolean(context.registrationGrant)
        const tombstonePresent = Boolean(state.tombstones && state.tombstones[authorityId])
        // Releases shipped before this repair could durably accept an
        // explicitly authorized re-login while retaining the old logout
        // tombstone. accepted + its server intent is the durable proof that
        // the later login committed; a real subsequent logout clears
        // accepted in the same fenced transition that writes its tombstone.
        const acceptedLoginRepair = Boolean(tombstonePresent
          && accepted
          && sameGroup
          && accepted.serverIntentIdentity)
        const clearsTombstone = modernSwitch || legacySwitch || watermarkSwitch || acceptedLoginRepair
        if (tombstoneBlocks(state, authorityId, tuple) || (tombstonePresent && !clearsTombstone)) {
          throw new CredentialStateError('Credential authority has been logged out')
        }
        if (switching && !modernSwitch && !legacySwitch && !watermarkSwitch && !registrationSwitch) {
          throw new CredentialStateError('Credential response cannot switch authority')
        }
        if (accepted && sameGroup) {
          if (compareAuthority(tuple, accepted.authorityFloor) < 0 || compareCredential(tuple, accepted.cookieWatermark) < 0) {
            throw new CredentialStateError('Credential response is below the accepted floor')
          }
          // CODE-R4-004: a genuinely new (above the already-known watermark)
          // same-authority success racing an in-flight factor verification
          // is not stale -- park it behind that operation's outcome instead
          // of rejecting it outright. A rv-only bump of an already-installed
          // token never enters this path (plan D-7b "Stale-success recovery").
          if (state.factor
            && state.factor.state === 'pending'
            && state.factor.authorityId === authorityId
            && tuple.ae < state.factor.minAuthEpoch
            && compareCredential(tuple, accepted.cookieWatermark) > 0) {
            throw factorPendingParkError()
          }
        }
        const authorityChanged = !accepted || switching || compareAuthority(tuple, accepted.authorityFloor) > 0
        const revision = authorityChanged
          ? state.credentialAuthorityRevision + 1
          : state.credentialAuthorityRevision
        const tombstones = { ...state.tombstones }
        if (clearsTombstone) delete tombstones[authorityId]
        const next = {
          ...state,
          credentialAuthorityRevision: revision,
          tombstones,
          accepted: {
            authorityId,
            authorityFloor: authorityChanged ? authorityFor(tuple) : accepted.authorityFloor,
            cookieWatermark: !accepted || switching || compareCredential(tuple, accepted.cookieWatermark) > 0
              ? tuple
              : accepted.cookieWatermark,
            serverIntentIdentity: context.serverIntentIdentity || (accepted && accepted.serverIntentIdentity) || null,
          },
        }
        return {
          state: next,
          value: {
            tuple, authorityId, revision, fenceToken,
          },
        }
      }, (_state, token, value) => ({
        commit: () => this.runReversibleInstall(() => {
          // CODE-R1-010: this runs while the fence's lock/lease is still held,
          // immediately after ownership was proven by the commit above -- no
          // greater cross-tab writer can install a newer authority until this
          // returns, so no presentation, token, broadcast, or auth event here
          // can ever be beaten by one.
          if (context.cachePresentation !== false) this.cachePresentation(data)
          if (isBrowser()) localStorage.removeItem(this.jwtConfig.parentAuthMarkerKeyName)
          this.setToken(data.accessToken, data.session, {
            token: data.accessToken,
            session: data.session || null,
            authorityId: value.authorityId,
            A: authorityFor(value.tuple),
            C: value.tuple,
            credentialAuthorityRevision: value.revision,
          })
          if (data.session && context.loginPayload && typeof context.loginPayload.remembrance === 'boolean') {
            this.session = { ...data.session, persistent: context.loginPayload.remembrance }
          }
          this.removeLegacyRefreshTokens()
        }),
        finalize: isCurrent => {
          if (!isCurrent()) return false
          this.broadcastCredentialState(token)
          dispatchAuthEvent('diko:auth-changed', { authenticated: true, session: this.session })
          return true
        },
      }))
    } catch (error) {
      if (error && error.code === FACTOR_PENDING_PARK) return this.parkBehindFactorOperation(data, context)
      throw error
    }
    if (!result.committed || !result.value) throw new CredentialStateError('Credential response lost its installation fence')
    return data
  }

  // CODE-R4-004: plan D-7b "Stale-success recovery" -- a same-authority
  // success arriving while a factor operation is pending waits for that
  // operation's outcome instead of being rejected. On pre-commit failure the
  // parked response is re-evaluated against the (unchanged) A/W exactly as
  // it would have been without the race. On success it is discarded and
  // this call resolves via exactly one coalesced W-bearing recovery -- never
  // notifying twice if this tab already reconciled independently (so
  // HostView cannot reconnect twice).
  async parkBehindFactorOperation(data, context) {
    const tuple = this.validateCredentialResponse(data)
    const authorityId = authorityIdFor(tuple)
    const { state } = readCredentialState()
    const { factor } = state
    if (!factor || factor.state !== 'pending' || factor.authorityId !== authorityId) {
      return this.acceptCredentialResponse(data, context)
    }
    const settled = await waitForFactorSettled(factor.operationId, Date.now() + FACTOR_RECOVERY_WINDOW_MS)
    if (settled && settled.state === 'ready' && settled.authorityId === authorityId) {
      const alreadyReconciled = this.credentialSlot && credentialsEqual(this.credentialSlot.C, settled.credentialVersion)
      if (!alreadyReconciled) await this.recoverFactorReadyAsSibling(settled)
      // CODE-R4-004: the parked (lower-version) response itself is discarded
      // -- the caller's promise resolves with the canonical, already-
      // installed W-bearing credential the factor operation produced, never
      // the stale token this response originally carried.
      return {
        ...data, accessToken: this.getToken(), session: this.session, credentialVersion: settled.credentialVersion,
      }
    }
    if (settled && settled.state === 'pending') {
      throw new CredentialStateError('Pending factor verification did not settle before the recovery window expired')
    }
    // CODE-R4-004: 'terminal' (400 -- a definite client-side rejection, e.g.
    // a wrong OTP code) and 'retryable' (503002 -- plan D-7b's explicit
    // "pre-commit 503002") are the only two dispositions durably PROVEN to
    // have never advanced the authority floor, so only they may fall
    // through to re-evaluate the parked response exactly as if the race had
    // never happened.
    if (settled && ['terminal', 'retryable'].includes(settled.state)) {
      return this.acceptCredentialResponse(data, context)
    }
    // Every other disposition here -- still 'restart-required' at the
    // deadline (a committed-or-ambiguous rotation /auth/factor/recover never
    // resolved) or a generic 'failed' (an ambiguous 5xx with no commit proof
    // either way) -- must never let the parked (lower-version) response be
    // silently reaccepted as though the authority never advanced.
    throw new CredentialStateError('Factor operation did not durably prove a precommit abort before the recovery window expired')
  }

  acceptAuthResponse(data, options = {}) {
    return this.acceptCredentialResponse(data, options)
  }

  acceptLegacyCredentialResponse(data, legacyLane) {
    return this.acceptCredentialResponse(data, { legacyLane })
  }

  currentCredentialVersion() {
    return tupleFromAccessToken(this.getToken())
  }

  async login(...args) {
    const response = await this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
    await this.acceptCredentialResponse(response.data, {
      loginPayload: args[0],
      allowInitialAuthority: true,
    })
    return response
  }

  // eslint-disable-next-line class-methods-use-this
  serverIntentIdentityFromToken(token) {
    const payload = decodeTokenPayload(token)
    if (!payload
      || payload.purpose !== 'login-intent'
      || payload.iss !== 'diko-auth'
      || payload.aud !== 'login-intent'
      || !payload.flowHash
      || !safeVersion(payload.generation)
      || !payload.jti
      || !safeVersion(payload.clientFence)
      || typeof payload.ownerNonce !== 'string'
      || !/^[A-Za-z0-9_-]{43}$/.test(payload.ownerNonce)
      || !safeVersion(payload.exp)
      || payload.exp * 1000 <= Date.now()) {
      throw new CredentialStateError('Login intent response is malformed')
    }
    return {
      flowHash: String(payload.flowHash),
      generation: Number(payload.generation),
      jti: String(payload.jti),
      clientFence: Number(payload.clientFence),
      ownerNonce: String(payload.ownerNonce),
      expiresAt: Number(payload.exp) * 1000,
    }
  }

  // eslint-disable-next-line class-methods-use-this
  intentMatchesState(identity, state) {
    const intent = state && state.loginIntent
    if (!identity || !intent || intent.phase === 'requesting') return false
    return intent.flowHash === identity.flowHash
      && intent.generation === identity.generation
      && intent.jti === identity.jti
      && intent.clientFence === identity.clientFence
      && intent.ownerNonce === identity.ownerNonce
      && safeVersion(intent.expiresAt)
      && intent.expiresAt > Date.now()
  }

  isCurrentServerIntent(identity) {
    try {
      return this.intentMatchesState(identity, readCredentialState().state)
    } catch (error) {
      return false
    }
  }

  // CODE-R4-003: durably promote the current login intent into the shared
  // journal's approved 'phone-setup' phase BEFORE the caller stores the
  // module-local phoneSetupHandoff or navigates -- a fenced write with a
  // read-back-verified commit (result.committed), not merely an in-memory
  // flag, so the continuation is ordered and visible across tabs exactly
  // like every other credential-state transition.
  async promotePhoneSetupIntent(identity, phoneSetupToken) {
    const payload = decodeTokenPayload(phoneSetupToken)
    if (!payload || payload.purpose !== 'phone-setup' || !payload.sub || !safeVersion(payload.exp)) {
      throw new CredentialStateError('Phone-setup token is malformed')
    }
    const tokenExpiresAt = payload.exp * 1000
    const result = await withCredentialStateFence(state => {
      if (!this.intentMatchesState(identity, state)) {
        throw new CredentialStateError('Phone-setup promotion belongs to a superseded login intent')
      }
      return {
        ...state,
        loginIntent: {
          ...state.loginIntent,
          phase: 'phone-setup',
          setupSub: String(payload.sub),
          tokenExpiresAt,
          continuationUntil: tokenExpiresAt + PHONE_SETUP_CONTINUATION_GRACE_MS,
        },
      }
    })
    if (!result.committed) throw new CredentialStateError('Phone-setup promotion lost its fence')
    this.broadcastCredentialState(result.token)
    return true
  }

  // CODE-R4-003: validated before every phone-setup request and acceptance
  // (PhoneSetup.vue) -- the exact identity (not just "some phone-setup phase
  // exists") must match, and the phase's own continuation deadline
  // (tokenExpiresAt + grace) is enforced here rather than trusted from the
  // caller's clock.
  isPhoneSetupContinuationCurrent = identity => {
    try {
      const { state } = readCredentialState()
      const intent = state.loginIntent
      if (!intent || intent.phase !== 'phone-setup' || !identity) return false
      return intent.flowHash === identity.flowHash
        && intent.generation === identity.generation
        && intent.jti === identity.jti
        && intent.clientFence === identity.clientFence
        && intent.ownerNonce === identity.ownerNonce
        && safeVersion(intent.continuationUntil)
        && Date.now() <= intent.continuationUntil
    } catch (error) {
      return false
    }
  };

  async postOrderedLogin(endpoint, payload) {
    const deadline = Date.now() + TRANSPORT_TIMEOUT_MS
    const config = {
      withCredentials: true,
      skipAuthToken: true,
      skipAuthRefresh: true,
      skipAuthErrorHandling: true,
    }
    for (let attempt = 0; attempt < 30; attempt += 1) {
      const remaining = deadline - Date.now()
      if (remaining <= 0) break
      // The same body is intentionally retried: intent requests are
      // idempotent by requestNonce and finalizers attach to the same grant.
      // eslint-disable-next-line no-await-in-loop
      const response = await this.axiosIns.post(endpoint, payload, {
        ...config,
        timeout: remaining,
      })
      if (response.status !== 202) return response
      const retryAfter = Number(response.data && response.data.retryAfterSeconds) || 1
      const delay = Math.min(1000, Math.max(100, retryAfter * 1000))
      if (Date.now() + delay >= deadline) break
      // eslint-disable-next-line no-await-in-loop
      await sleep(delay)
    }
    throw new CredentialStateError('Ordered login did not settle before its retry deadline')
  }

  async beginLoginIntent() {
    const requestNonce = randomBase64Url()
    const claim = await withCredentialStateFence((state, token) => {
      const browserFlowId = state.browserFlowId || randomBase64Url()
      return {
        state: {
          ...state,
          browserFlowId,
          loginIntent: {
            phase: 'requesting',
            clientFence: token.fence,
            ownerNonce: token.ownerNonce,
            requestNonce,
          },
        },
        value: {
          browserFlowId, clientFence: token.fence, ownerNonce: token.ownerNonce, requestNonce,
        },
      }
    })
    if (!claim.committed) throw new CredentialStateError('Login intent order was superseded')
    this.broadcastCredentialState(claim.token)
    const request = claim.value
    const response = await this.postOrderedLogin(
      this.jwtConfig.loginIntentEndpoint,
      request,
    )
    const token = response.data && response.data.loginIntentToken
    const identity = this.serverIntentIdentityFromToken(token)
    if (identity.clientFence !== request.clientFence || identity.ownerNonce !== request.ownerNonce) {
      throw new CredentialStateError('Login intent response has the wrong client order')
    }
    const accepted = await withCredentialStateFence(state => {
      const pending = state.loginIntent
      if (!pending
        || pending.phase !== 'requesting'
        || pending.clientFence !== request.clientFence
        || pending.ownerNonce !== request.ownerNonce
        || pending.requestNonce !== request.requestNonce) {
        throw new CredentialStateError('Login intent response was superseded')
      }
      return {
        ...state,
        loginIntent: {
          phase: 'intent',
          ...identity,
          requestNonce,
        },
      }
    })
    if (!accepted.committed) throw new CredentialStateError('Login intent response lost its fence')
    this.currentLoginIntent = { token, identity, requestNonce }
    this.currentLoginGrant = null
    this.broadcastCredentialState(accepted.token)
    if (response.data.priorCommittedWatermark) {
      try {
        await this.reconcilePriorLoginCommit(response.data.priorCommittedWatermark)
      } catch (error) {
        // The prior-commit fence only exists so a fresh login never silently
        // stomps a LIVE committed session held by a concurrent tab. A session
        // the server itself declares expired or replaced can no longer be
        // stomped, so there is nothing left to reconcile and this login
        // continues. Every other failure -- including a refresh that succeeded
        // but installed below the reported floor -- still fails it closed.
        if (!isDeadSessionError(error)) throw error
      }
    }
    return { ...this.currentLoginIntent, priorCommittedWatermark: response.data.priorCommittedWatermark || null }
  }

  async reconcilePriorLoginCommit(watermark) {
    if (!watermark || typeof watermark !== 'object' || typeof watermark.accountType !== 'string') {
      throw new CredentialStateError('Prior committed login watermark is malformed')
    }
    // CODE-R3-004: the server sends two structurally different descriptor
    // shapes here (server: loginCoordination.js#descriptorFor) -- a Parent
    // commit carries no session/tuple fields at all, since a Parent account
    // has no client credential tuple for this client to ever reconcile
    // against. Only a User commit is a tuple this client's own accepted
    // credential can satisfy.
    if (watermark.accountType === 'Parent') return true
    if (watermark.accountType !== 'User') {
      throw new CredentialStateError('Prior committed login watermark is malformed')
    }
    const floor = normalizeCredential(watermark)
    if (!floor) throw new CredentialStateError('Prior committed login watermark is malformed')
    const { state } = readCredentialState()
    // A numerically-greater-but-unrelated authority must never be treated as
    // already satisfying this specific prior User commit -- same userId/sid
    // is required before any epoch/version comparison is meaningful.
    if (state.accepted
      && sameAuthorityId(state.accepted.cookieWatermark, floor)
      && compareCredential(state.accepted.cookieWatermark, floor) >= 0) return true
    // CODE-R3-004: authorize acceptance of exactly this reported prior
    // watermark, even when it lands on a different userId/sid than whatever
    // this client currently has accepted -- ordinary credential acceptance
    // must never treat that as an unauthorized authority switch here.
    await this.refreshToken(floor, { priorWatermark: floor })
    const selected = readCredentialState().state.accepted
    if (!selected
      || !sameAuthorityId(selected.cookieWatermark, floor)
      || compareCredential(selected.cookieWatermark, floor) < 0) {
      throw new CredentialStateError('Prior committed login could not be reconciled')
    }
    return true
  }

  async prepareLogin(payload, intent = this.currentLoginIntent) {
    if (!intent || !this.isCurrentServerIntent(intent.identity)) {
      throw new CredentialStateError('Login intent is not current')
    }
    const response = await this.axiosIns.post(
      this.jwtConfig.loginEndpoint,
      { ...payload, loginIntentToken: intent.token },
      {
        timeout: TRANSPORT_TIMEOUT_MS,
        withCredentials: true,
        skipAuthToken: true,
        skipAuthRefresh: true,
        skipAuthErrorHandling: true,
      },
    )
    if (!this.isCurrentServerIntent(intent.identity)) throw new CredentialStateError('Login prepare response was superseded')
    if (response.data && response.data.loginGrant) {
      this.currentLoginGrant = { value: response.data.loginGrant, identity: intent.identity }
    }
    return response
  }

  async finalizeLogin(options = {}) {
    const intent = options.intent || this.currentLoginIntent
    const grant = options.loginGrant || (this.currentLoginGrant && this.currentLoginGrant.value)
    if (!intent || !grant || !this.isCurrentServerIntent(intent.identity)) {
      throw new CredentialStateError('Login grant is not current')
    }
    const response = await this.postOrderedLogin(
      this.jwtConfig.loginFinalizeEndpoint,
      { loginIntentToken: intent.token, loginGrant: grant },
    )
    // postOrderedLogin only resolves once the server durably committed the
    // login, so every failure below is post-commit: the session is live even
    // though this client could not order or install it. Mark those errors so a
    // caller that opts in can reconcile the commit in place instead of
    // reporting a login that actually succeeded. The marker deliberately
    // carries no access token or raw envelope -- it travels on an error object
    // that callers hand to toast/logging paths.
    try {
      if (!this.isCurrentServerIntent(intent.identity)) throw new CredentialStateError('Login finalize response was superseded')
      // CODE-R4-004: if this finalize response raced a pending factor
      // operation for the same already-accepted authority, acceptCredentialResponse
      // may park and discard it, resolving instead with the canonical
      // recovered result -- callers must see that canonical result, never the
      // raw (possibly stale/discarded) response.
      const accepted = await this.acceptCredentialResponse(response.data, {
        serverIntentIdentity: intent.identity,
        loginPayload: options.loginPayload || null,
      })
      this.currentLoginGrant = null
      return { ...response, data: accepted }
    } catch (error) {
      const data = response.data || {}
      // A Parent commit has no client credential tuple to reconcile against
      // (see reconcilePriorLoginCommit), so it is never marked.
      const floor = data.isParent ? null : normalizeCredential(data.credentialVersion)
      if (floor && error instanceof CredentialStateError) {
        error.committedLogin = {
          watermark: { accountType: 'User', ...floor },
          presentation: { isParent: false, user: data.user || null },
        }
      }
      throw error
    }
  }

  async register(...args) {
    const response = await this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
    const accepted = await this.acceptCredentialResponse(response.data, {
      loginPayload: { remembrance: false },
      registrationGrant: true,
    })
    return { ...response, data: accepted }
  }

  async armFactorPending(challengeId, endpoint = null) {
    const token = this.getToken()
    const tuple = tupleFromAccessToken(token)
    if (!tuple || !challengeId) throw new CredentialStateError('Current credential cannot authorize factor verification')
    if (tuple.ae >= Number.MAX_SAFE_INTEGER) throw new CredentialStateError('Factor authority is exhausted')
    const operationId = randomUuid()
    const recoverySecret = randomBase64Url()
    const authorityId = authorityIdFor(tuple)
    const nonce = randomBase64Url()
    const recoveryDeadline = Date.now() + FACTOR_RECOVERY_WINDOW_MS
    const now = Date.now()
    const result = await withCredentialStateFence((state, fenceToken) => {
      if (!state.accepted
        || state.accepted.authorityId !== authorityId
        || compareAuthority(tuple, state.accepted.authorityFloor) < 0
        || tombstoneBlocks(state, authorityId, tuple)) {
        throw new CredentialStateError('Factor verification authority is stale')
      }
      // CODE-R1-013: a restart-required factor operation is dead — its
      // recovery secret may already have been consumed by a prior recovery
      // attempt — so it can never be silently re-armed. The caller must
      // reload rather than retry in place.
      if (state.factor && state.factor.state === 'restart-required') {
        throw new CredentialStateError('Factor verification requires a restart')
      }
      if (state.factor
        && state.factor.state === 'pending'
        && state.factor.operationId !== operationId
        && state.factor.recoveryDeadline > Date.now()) {
        throw new CredentialStateError('Another factor verification is already pending')
      }
      return {
        ...state,
        factor: {
          state: 'pending',
          nonce,
          operationId,
          fence: fenceToken.fence,
          authorityId,
          userId: tuple.userId,
          sid: tuple.sid,
          minAuthEpoch: tuple.ae + 1,
          expectedSessionEpoch: tuple.se + 1,
          issuedAt: now,
          expiresAt: now + FACTOR_RECOVERY_WINDOW_MS,
          recoveryDeadline,
        },
      }
    })
    if (!result.committed) throw new CredentialStateError('Factor pending state lost its fence')
    this.factorOperation = {
      challengeId,
      operationId,
      recoverySecret,
      nonce,
      authorityId,
      userId: tuple.userId,
      sid: tuple.sid,
      minAuthEpoch: tuple.ae + 1,
      expectedSessionEpoch: tuple.se + 1,
      fence: result.token && result.token.fence,
      recoveryDeadline,
      endpoint,
    }
    this.broadcastCredentialState(result.token)
    return {
      challengeId,
      operationId,
      recoverySecret,
      fence: result.token && result.token.fence,
      authorityId,
      endpoint,
    }
  }

  async markFactorState(stateName, operationId, extra = {}) {
    const result = await withCredentialStateFence(state => {
      const { factor } = state
      if (!factor || factor.operationId !== operationId) return state
      return { ...state, factor: { ...factor, ...extra, state: stateName } }
    })
    if (result.committed) this.broadcastCredentialState(result.token)
    return result
  }

  async acceptFactorReissue(data, operation = this.factorOperation) {
    if (!operation) throw new CredentialStateError('No factor operation is pending')
    const tuple = this.validateCredentialResponse(data)
    // CODE-R1-013: a factor rotation is a server-defined EXACT transition —
    // ae+1/se+1/rv=0 (server: authSession.js commitSessionRotation) — not any
    // credential at or above a floor. Accepting a merely-higher epoch would
    // let an unrelated concurrent rotation masquerade as this operation's result.
    if (tuple.userId !== operation.userId
      || tuple.sid !== operation.sid
      || tuple.ae !== operation.minAuthEpoch
      || tuple.se !== operation.expectedSessionEpoch
      || tuple.rv !== 0) {
      throw new CredentialStateError('Factor credential does not satisfy the pending authority')
    }
    const { authorityId } = operation
    const issuedAt = Date.now()
    // CODE-R1-013: install the higher A/W and mark the factor operation
    // 'ready' inside ONE fenced transition+install, not two separate fences
    // -- a sibling tab (or this same tab's SESSION_REPLACED handler, or a
    // BroadcastChannel listener) must never be able to observe the higher
    // authority while the journal's factor state still reads 'pending'.
    const result = await withCredentialStateFence((state, fenceToken) => {
      const { factor, accepted } = state
      // A prior recovery attempt within this same operation may have marked
      // the shared state 'restart-required' before a later attempt actually
      // succeeded; that is still this operation completing, not a stale one.
      if (!factor
        || !['pending', 'restart-required'].includes(factor.state)
        || factor.operationId !== operation.operationId
        || factor.authorityId !== operation.authorityId) {
        throw new CredentialStateError('Factor operation is no longer current')
      }
      if (tombstoneBlocks(state, authorityId, tuple)) {
        throw new CredentialStateError('Credential authority has been logged out')
      }
      if (accepted
        && (compareAuthority(tuple, accepted.authorityFloor) < 0 || compareCredential(tuple, accepted.cookieWatermark) < 0)) {
        throw new CredentialStateError('Credential response is below the accepted floor')
      }
      const authorityChanged = !accepted || compareAuthority(tuple, accepted.authorityFloor) > 0
      const revision = authorityChanged
        ? state.credentialAuthorityRevision + 1
        : state.credentialAuthorityRevision
      return {
        state: {
          ...state,
          credentialAuthorityRevision: revision,
          accepted: {
            authorityId,
            authorityFloor: authorityChanged ? authorityFor(tuple) : accepted.authorityFloor,
            cookieWatermark: !accepted || compareCredential(tuple, accepted.cookieWatermark) > 0
              ? tuple
              : accepted.cookieWatermark,
            serverIntentIdentity: (accepted && accepted.serverIntentIdentity) || null,
          },
          factor: {
            ...factor,
            state: 'ready',
            credentialVersion: tuple,
            fence: fenceToken.fence,
            issuedAt,
            expiresAt: issuedAt + FACTOR_READY_TTL_MS,
          },
        },
        value: {
          tuple, authorityId, revision, fenceToken, issuedAt,
        },
      }
    }, (_committedState, token, value) => ({
      commit: () => this.runReversibleInstall(() => {
        if (isBrowser()) localStorage.removeItem(this.jwtConfig.parentAuthMarkerKeyName)
        this.setToken(data.accessToken, data.session, {
          token: data.accessToken,
          session: data.session || null,
          authorityId: value.authorityId,
          A: authorityFor(value.tuple),
          C: value.tuple,
          credentialAuthorityRevision: value.revision,
        })
        this.removeLegacyRefreshTokens()
        this.factorOperation = null
      }),
      finalize: isCurrent => {
        if (!isCurrent()) return false
        this.broadcastCredentialState(token)
        dispatchAuthEvent('diko:auth-changed', { authenticated: true, session: this.session })
        this.tokenReissuedListeners.forEach(listener => listener({
          credentialVersion: value.tuple,
          operationId: operation.operationId,
          authorityId: value.authorityId,
          fence: value.fenceToken.fence,
          issuedAt: value.issuedAt,
          expiresAt: value.issuedAt + FACTOR_READY_TTL_MS,
        }))
        dispatchAuthEvent('diko:factor-ready', {
          credentialVersion: value.tuple,
          operationId: operation.operationId,
          authorityId: value.authorityId,
          fence: value.fenceToken.fence,
          issuedAt: value.issuedAt,
          expiresAt: value.issuedAt + FACTOR_READY_TTL_MS,
        })
        return true
      },
    }))
    if (!result.committed) throw new CredentialStateError('Factor ready transition lost its fence')
    return data
  }

  onTokenReissued(listener) {
    if (typeof listener !== 'function') return () => {}
    this.tokenReissuedListeners.add(listener)
    return () => this.tokenReissuedListeners.delete(listener)
  }

  // eslint-disable-next-line class-methods-use-this
  isCallerFactorPending(payload = {}) {
    let factor
    try {
      factor = readCredentialState().state.factor
    } catch (error) {
      return false
    }
    if (!factor || !['pending', 'ready', 'restart-required'].includes(factor.state)) return false
    if (factor.state === 'ready' && isFactorReadyExpired(factor)) return false
    if (factor.state === 'pending' && factor.recoveryDeadline && factor.recoveryDeadline <= Date.now()) return false
    const userId = payload.userId || payload.accountId
    const sid = payload.sid || payload.sessionId
    if (userId && String(userId) !== String(factor.userId)) return false
    if (sid && String(sid) !== String(factor.sid)) return false
    if (payload.operationId && payload.operationId !== factor.operationId) return false
    return true
  }

  async runFactorVerification(endpoint, { challengeId, code }, options = {}) {
    const carrier = await this.armFactorPending(challengeId, endpoint)
    try {
      const response = await this.axiosIns.post(
        endpoint,
        {
          challengeId,
          code,
          operationId: carrier.operationId,
          recoverySecret: carrier.recoverySecret,
        },
        {
          ...(options.requestConfig || {}),
          timeout: TRANSPORT_TIMEOUT_MS,
          factorReissueRequest: true,
          suppressErrorToast: true,
          skipAuthRefresh: true,
          skipAuthErrorHandling: true,
        },
      )
      if (response.status === 202 || (response.data && response.data.status === 'pending')) {
        return this.recoverFactorOperation()
      }
      if (response.data && response.data.status === 'retryable') {
        await this.markFactorState('retryable', carrier.operationId)
        this.factorOperation = null
        throw factorOutcomeError(503, 503002, 'Factor verification is retryable')
      }
      await this.acceptFactorReissue(response.data)
      return response.data
    } catch (error) {
      const status = error && error.response && error.response.status
      const codeValue = Number(responseCode(error))
      if (!error.response || error.code === 'ECONNABORTED') return this.recoverFactorOperation()
      if (status === 503 && codeValue === 503003) {
        await this.markFactorState('restart-required', carrier.operationId)
        dispatchAuthEvent('diko:factor-restart-required', { operationId: carrier.operationId })
        throw error
      }
      if (status === 503 && codeValue === 503002) {
        await this.markFactorState('retryable', carrier.operationId)
        this.factorOperation = null
        throw error
      }
      if (status === 400) {
        if (hasExactFactorTerminalEvidence(error, carrier, endpoint)) {
          await this.markFactorState('terminal', carrier.operationId)
          this.factorOperation = null
          throw error
        }
        await this.markFactorState('failed', carrier.operationId)
        this.factorOperation = null
        throw error
      }
      if (status >= 500) {
        await this.markFactorState('failed', carrier.operationId)
        this.factorOperation = null
        throw error
      }
      throw error
    }
  }

  recoverFactorOperation() {
    if (this.factorRecoveryPromise) return this.factorRecoveryPromise
    const operation = this.factorOperation
    if (!operation) return Promise.reject(new CredentialStateError('Factor recovery secret is unavailable'))
    this.factorRecoveryPromise = (async () => {
      let delay = 250
      /* eslint-disable no-await-in-loop */
      while (Date.now() < operation.recoveryDeadline) {
        try {
          const response = await this.axiosIns.post(
            this.jwtConfig.factorRecoverEndpoint,
            {
              challengeId: operation.challengeId,
              operationId: operation.operationId,
              recoverySecret: operation.recoverySecret,
            },
            {
              timeout: TRANSPORT_TIMEOUT_MS,
              withCredentials: true,
              skipAuthToken: true,
              skipAuthRefresh: true,
              skipAuthErrorHandling: true,
              suppressErrorToast: true,
            },
          )
          if (response.status === 202 || (response.data && response.data.status === 'pending')) {
            await sleep(delay)
            delay = Math.min(delay * 2, 4000)
          } else if (response.data && response.data.status === 'retryable') {
            await this.markFactorState('retryable', operation.operationId)
            this.factorOperation = null
            throw factorOutcomeError(503, 503002, 'Factor verification is retryable')
          } else {
            await this.acceptFactorReissue(response.data, operation)
            return response.data
          }
        } catch (error) {
          const status = error && error.response && error.response.status
          const codeValue = Number(responseCode(error))
          if (!error.response || error.code === 'ECONNABORTED') {
            await sleep(delay)
            delay = Math.min(delay * 2, 4000)
          } else if (status === 503 && codeValue === 503003) {
            await this.markFactorState('restart-required', operation.operationId)
            dispatchAuthEvent('diko:factor-restart-required', { operationId: operation.operationId })
            throw error
          } else if (status === 503 && codeValue === 503002) {
            throw error
          } else if (status === 400) {
            const isTerminal = hasExactFactorTerminalEvidence(error, operation, this.jwtConfig.factorRecoverEndpoint)
            await this.markFactorState(isTerminal ? 'terminal' : 'failed', operation.operationId)
            this.factorOperation = null
            throw error
          } else {
            await this.markFactorState('failed', operation.operationId)
            this.factorOperation = null
            throw error
          }
        }
      }
      /* eslint-enable no-await-in-loop */
      await this.markFactorState('failed', operation.operationId, { reason: 'recovery-expired' })
      this.factorOperation = null
      throw new CredentialStateError('Factor recovery window expired')
    })().finally(() => {
      this.factorRecoveryPromise = null
    })
    return this.factorRecoveryPromise
  }

  async bootstrap() {
    if (this.isParentSession()) return true
    if (this.bootstrapPromise) return this.bootstrapPromise
    this.bootstrapPromise = (async () => {
      try {
        await this.ensureFreshAccessToken(30)
        return true
      } catch (error) {
        this.clearPractitionerState()
        return false
      } finally {
        this.bootstrapPromise = null
      }
    })()
    return this.bootstrapPromise
  }

  async ensureFreshAccessToken(minValiditySeconds = 30) {
    if (this.isParentSession()) return this.getToken()
    if (this.isAccessTokenValid(minValiditySeconds)) return this.accessToken
    return this.refreshToken()
  }

  // eslint-disable-next-line class-methods-use-this
  credentialFloorHeaders(floor) {
    if (!isBrowser()) return {}
    if (floor) return { [CREDENTIAL_FLOOR_HEADER]: base64UrlJson(floor) }
    const { state } = readCredentialState()
    if (!state.accepted || !state.accepted.cookieWatermark) return {}
    return { [CREDENTIAL_FLOOR_HEADER]: base64UrlJson(state.accepted.cookieWatermark) }
  }

  async observeCredentialState() {
    if (!this.credentialSlot) return false
    const { state } = readCredentialState()
    const { accepted } = state
    if (!accepted || accepted.authorityId !== this.credentialSlot.authorityId) {
      this.clearPractitionerState()
      return false
    }
    if (tombstoneBlocks(state, this.credentialSlot.authorityId, this.credentialSlot.C)) {
      this.clearPractitionerState()
      return false
    }
    if (compareAuthority(accepted.authorityFloor, this.credentialSlot.A) > 0) {
      await this.recoverCredentialAuthority()
      return true
    }
    return false
  }

  async recoverCredentialAuthority() {
    if (this.factorOperation) return this.recoverFactorOperation()
    const { state } = readCredentialState()
    if (state.factor && state.factor.state === 'pending') {
      dispatchAuthEvent('diko:factor-recovery-required', {
        operationId: state.factor.operationId,
        authorityId: state.factor.authorityId,
      })
      // CODE-R1-013: a caller observing 'pending' (chiefly the
      // SESSION_REPLACED direct-server-signal path in handleResponseError,
      // which can genuinely race the owning tab's in-flight verification)
      // must wait for that operation's outcome rather than giving up
      // immediately -- giving up here is exactly what forced a stale-token
      // replay before ready became authoritative. Wait through the
      // operation's OWN bounded recovery deadline (never a shorter fixed
      // window) -- a legitimate factor verification can genuinely take
      // longer than a few seconds.
      const settled = await waitForFactorSettled(state.factor.operationId, state.factor.recoveryDeadline)
      if (settled && settled.state === 'ready' && settled.operationId === state.factor.operationId) {
        if (isFactorReadyExpired(settled)) return this.refreshToken()
        return this.recoverFactorReadyAsSibling(settled)
      }
      // CODE-R1-013: still pending past its own deadline, restart-required,
      // failed, terminal, or retryable are none of them a proven ready
      // tuple -- never replay the caller's revoked pre-rotation bearer
      // against any of them; fail closed instead so the caller's own retry
      // (or a later, independent recovery) is what eventually succeeds.
      throw new CredentialStateError('Factor operation did not produce a ready credential before its recovery deadline')
    }
    // CODE-R1-013: a sibling tab (no local factorOperation) observing a
    // completed factor rotation must reconcile through the same
    // token-reissued/factor-ready notification path the owning tab uses, so
    // HostView's socket reconnects here too — not just in the tab that ran
    // the verification.
    if (state.factor && state.factor.state === 'ready') {
      if (isFactorReadyExpired(state.factor)) return this.refreshToken()
      return this.recoverFactorReadyAsSibling(state.factor)
    }
    return this.refreshToken()
  }

  async recoverFactorReadyAsSibling(factor) {
    const token = await this.refreshToken()
    const tuple = this.currentCredentialVersion()
    if (!tuple) {
      throw new CredentialStateError('Factor-ready recovery did not install a credential')
    }
    const reportTuple = factor && factor.credentialVersion && credentialsEqual(tuple, factor.credentialVersion)
      ? factor.credentialVersion
      : tuple
    const sameAuthority = factor && factor.userId && factor.sid && sameAuthorityId(tuple, factor)
    if (!sameAuthority) {
      throw new CredentialStateError('Factor-ready recovery tuple is not comparable with this factor operation')
    }
    this.tokenReissuedListeners.forEach(listener => listener({
      credentialVersion: reportTuple,
      operationId: factor.operationId,
      authorityId: factor.authorityId,
      fence: factor.fence,
      issuedAt: factor.issuedAt,
      expiresAt: factor.expiresAt,
    }))
    dispatchAuthEvent('diko:factor-ready', {
      credentialVersion: reportTuple,
      operationId: factor.operationId,
      authorityId: factor.authorityId,
      fence: factor.fence,
      issuedAt: factor.issuedAt,
      expiresAt: factor.expiresAt,
    })
    return token
  }

  refreshToken(floor, acceptContext = {}) {
    // CODE-R3-004: a floor-targeted refresh (reconciling a specific prior
    // committed authority) must never be satisfied by the ambient
    // single-flight promise of an unrelated in-progress refresh, nor by the
    // "current token is already valid" shortcut below -- the current token
    // being valid says nothing about whether it satisfies THIS floor.
    if (floor) return this.withRefreshLock(() => this.performRefresh(floor, acceptContext))
    if (this.refreshPromise) return this.refreshPromise
    const previousToken = this.accessToken
    this.refreshPromise = this.withRefreshLock(async () => {
      if (this.accessToken && this.accessToken !== previousToken && this.isAccessTokenValid(30)) {
        return this.accessToken
      }
      return this.performRefresh(undefined, acceptContext)
    }).finally(() => {
      this.refreshPromise = null
    })
    return this.refreshPromise
  }

  async performRefresh(floor, acceptContext = {}) {
    const deadline = Date.now() + TRANSPORT_TIMEOUT_MS
    let requestedFloor = floor
    let lastError = null
    for (let attempt = 0; attempt < MAX_REFRESH_INSTALL_ATTEMPTS; attempt += 1) {
      const remainingMs = deadline - Date.now()
      if (remainingMs <= 0 && lastError) throw lastError
      // eslint-disable-next-line no-await-in-loop
      const response = await this.axiosIns.post(
        this.jwtConfig.refreshEndpoint,
        null,
        {
          withCredentials: true,
          headers: this.credentialFloorHeaders(requestedFloor),
          timeout: Math.max(1, remainingMs),
          skipAuthToken: true,
          skipAuthRefresh: true,
          skipAuthErrorHandling: true,
        },
      )
      try {
        // CODE-R4-004: a parked (stale-success) response is discarded and
        // replaced by a coalesced recovery -- the resolved token must be the
        // canonical one acceptCredentialResponse actually installed, never the
        // raw response's own accessToken, which the park path may have thrown away.
        // eslint-disable-next-line no-await-in-loop
        const accepted = await this.acceptCredentialResponse(response.data, acceptContext)
        return accepted.accessToken
      } catch (error) {
        lastError = error
        const tuple = normalizeCredential(response.data && response.data.credentialVersion)
        let accepted = null
        let credentialState = null
        try {
          credentialState = readCredentialState().state
          accepted = credentialState.accepted
        } catch (stateError) {
          accepted = null
        }
        const earlyFenceRace = error instanceof CredentialStateError
          && ['Credential-state fence was superseded', 'Credential-state transition lost its fence'].includes(error.message)
        const lateInstallRace = error instanceof CredentialStateError
          && ['Credential response is below the accepted floor', 'Credential response lost its installation fence'].includes(error.message)
        const sameAcceptedAuthority = accepted && tuple && sameAuthorityId(accepted.cookieWatermark, tuple)
        const responseAuthorityId = tuple && authorityIdFor(tuple)
        const responseIsTombstoned = credentialState && responseAuthorityId
          ? tombstoneBlocks(credentialState, responseAuthorityId, tuple)
          : false
        const installRace = tuple
          && !responseIsTombstoned
          && ((earlyFenceRace && (!accepted || sameAcceptedAuthority)) || (lateInstallRace && sameAcceptedAuthority))
        if (!installRace || attempt === MAX_REFRESH_INSTALL_ATTEMPTS - 1) throw error
        requestedFloor = sameAcceptedAuthority ? accepted.cookieWatermark : requestedFloor
        // Let the winner finish publishing its cookie/journal state before
        // this loser asks the server for a credential at the new W floor.
        // eslint-disable-next-line no-await-in-loop
        await sleep(Math.min(25 * (attempt + 1), Math.max(1, deadline - Date.now())))
      }
    }
    throw lastError || new CredentialStateError('Refresh credential could not be installed')
  }

  // eslint-disable-next-line class-methods-use-this
  async withRefreshLock(task) {
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
      return navigator.locks.request(AUTH_CHANNEL, { mode: 'exclusive' }, task)
    }
    // localStorage is not an atomic cross-agent mutex. Concurrent fallback
    // refreshes are reconciled by performRefresh against the winning W floor.
    return task()
  }

  isRefreshableAuthError(error) {
    if (!this.jwtConfig) return false
    const status = error && error.response && error.response.status
    const code = responseCode(error)
    return (status === 401 && (String(code) === '401018' || code === SESSION_EXPIRED))
      || (status === 403 && String(code) === '403007')
  }

  isSessionReplacedError(error) {
    if (!this.jwtConfig) return false
    const data = error && error.response && error.response.data
    return responseCode(error) === SESSION_REPLACED
      || Boolean(data && data.reason === 'device_slot_replaced')
  }

  async handleResponseError(error) {
    const response = error && error.response
    const originalRequest = error && error.config

    if (originalRequest && originalRequest.skipAuthErrorHandling) return Promise.reject(error)

    if (this.isSessionReplacedError(error)) {
      if (this.isCallerFactorPending(response && response.data)) {
        try {
          await this.recoverCredentialAuthority()
        } catch (recoveryError) {
          return Promise.reject(recoveryError)
        }
        // CODE-R1-013: replay exactly the request that observed the
        // supersession, exactly once, now that recovery has installed a
        // fresh credential — never a broader retry-storm, and never a
        // replay against a recovery that only reported "still pending".
        if (originalRequest && !originalRequest.dikoFactorRetry) {
          const token = this.getToken()
          if (token) {
            originalRequest.dikoFactorRetry = true
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${token}`
            return this.axiosIns(originalRequest)
          }
        }
      } else {
        this.handleSessionReplaced(response.data)
      }
      return Promise.reject(error)
    }

    if (originalRequest
      && !originalRequest.skipAuthRefresh
      && !originalRequest.dikoAuthRetry
      && this.accountType !== ACCOUNT_PARENT
      && this.isRefreshableAuthError(error)) {
      // eslint-disable-next-line no-param-reassign
      originalRequest.dikoAuthRetry = true
      try {
        const token = await this.refreshToken()
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${token}`
        return this.axiosIns(originalRequest)
      } catch (refreshError) {
        if (this.isSessionReplacedError(refreshError)) {
          this.handleSessionReplaced(refreshError.response.data)
        } else {
          this.clearPractitionerState()
          dispatchAuthEvent('diko:auth-required', { reason: SESSION_EXPIRED })
        }
        return Promise.reject(refreshError)
      }
    }

    if (response && response.status === 402) {
      invalidateSubscriptionStanding()
      presentSubscriptionActionOutcome({
        outcome: SubscriptionActionOutcome.SUBSCRIPTION_REQUIRED,
        reason: response.data && response.data.reason,
        errorCode: response.data && response.data.errorCode,
        suppressToast: Boolean(originalRequest && originalRequest.suppressErrorToast),
      })
      // A missing subscription is an authorization/business-state outcome,
      // not an expired login. Keep the current credential intact and let the
      // app router take the user to the purchase page.
      return Promise.reject(error)
    }

    // Post-pending pipeline handoff §2: 403012 means the subscription is fine
    // but approval standing blocks STARTING new gated work. Like 402 it is a
    // business-state outcome, not an auth failure — keep the credential and
    // let the app router take the user to the application screen. Unlike the
    // 402 branch, the toast honors suppressErrorToast so a call site that
    // renders its own dialog does not double-display.
    if (response && response.status === 403 && Number(responseCode(error)) === 403012) {
      invalidateSubscriptionStanding()
      const reason = response.data && response.data.reason
      let outcome = SubscriptionActionOutcome.APPROVAL_DENIED
      if (reason === SubscriptionActionOutcome.APPROVAL_MISSING) {
        outcome = SubscriptionActionOutcome.APPROVAL_MISSING
      } else if (reason === SubscriptionActionOutcome.APPROVAL_REJECTED) {
        outcome = SubscriptionActionOutcome.APPROVAL_REJECTED
      }
      presentSubscriptionActionOutcome({
        outcome,
        errorCode: Number(responseCode(error)),
        suppressToast: Boolean(originalRequest && originalRequest.suppressErrorToast),
      })
      return Promise.reject(error)
    }

    // Handoff §2: an admin-blocked account (403013) gets "contact us"
    // messaging only. No navigation event — blocked-state UI is data-driven
    // (accountBlocked on the status/user payloads), and the user may already
    // be on the screen a hop would target.
    if (response && response.status === 403 && Number(responseCode(error)) === 403013) {
      invalidateSubscriptionStanding()
      presentSubscriptionActionOutcome({
        outcome: SubscriptionActionOutcome.ACCOUNT_BLOCKED,
        errorCode: Number(responseCode(error)),
        suppressToast: Boolean(originalRequest && originalRequest.suppressErrorToast),
      })
      return Promise.reject(error)
    }

    if (response) {
      const data = response.data || {}
      if (!(error.config && error.config.suppressErrorToast) && String(data.errorCode) !== '101101') {
        Vue.$toast({
          component: ToastificationContent,
          props: {
            title: `ERROR ${data.errorCode || response.status}`,
            text: `${data.message || response.statusText || i18n.t(data.errorCode)}`,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            timeout: 2000,
          },
        })
      }
    }
    return Promise.reject(error)
  }

  clearPractitionerState() {
    if (this.accountType === ACCOUNT_PARENT) return
    invalidateSubscriptionStanding()
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = null
    this.accessToken = null
    this.accessTokenExpiresAt = 0
    this.accountType = null
    this.session = null
    this.credentialSlot = null
  }

  clearAuthState(options = {}) {
    const { broadcast = true } = options
    invalidateSubscriptionStanding()
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = null
    this.accessToken = null
    this.accessTokenExpiresAt = 0
    this.accountType = null
    this.session = null
    this.credentialSlot = null
    this.factorOperation = null
    if (isBrowser()) {
      [localStorage, sessionStorage].forEach(storage => {
        storage.removeItem(this.jwtConfig.storageTokenKeyName)
        storage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
      })
      localStorage.removeItem(this.jwtConfig.parentAuthMarkerKeyName)
      localStorage.removeItem('userData')
      localStorage.removeItem('userName')
      localStorage.removeItem('userAvatar')
    }
    ability.update(initialAbility)
    if (broadcast) this.broadcast('logout')
    dispatchAuthEvent('diko:auth-changed', { authenticated: false })
  }

  handleSessionReplaced(payload = {}, broadcast = true) {
    if (this.isCallerFactorPending(payload)) return false
    this.clearPractitionerState()
    ability.update(initialAbility)
    if (broadcast) this.broadcastCredentialState(readCredentialState().token)
    dispatchAuthEvent('diko:session-replaced', payload)
    return true
  }

  async logout() {
    const practitioner = this.accountType === ACCOUNT_USER
    if (practitioner) {
      await this.axiosIns.post(
        this.jwtConfig.logoutEndpoint,
        null,
        {
          withCredentials: true,
          headers: this.credentialFloorHeaders(),
          skipAuthRefresh: true,
          skipAuthErrorHandling: true,
        },
      )
      const authorityId = this.credentialSlot && this.credentialSlot.authorityId
      if (authorityId) {
        const result = await withCredentialStateFence(state => {
          const logoutWatermark = state.accepted && state.accepted.authorityId === authorityId
            ? state.accepted.cookieWatermark
            : this.credentialSlot.C
          return {
            ...state,
            credentialAuthorityRevision: state.credentialAuthorityRevision + 1,
            accepted: state.accepted && state.accepted.authorityId === authorityId ? null : state.accepted,
            factor: state.factor && state.factor.authorityId === authorityId ? null : state.factor,
            tombstones: { ...state.tombstones, [authorityId]: logoutWatermark },
          }
        })
        if (!result.committed) throw new CredentialStateError('Logout tombstone lost its fence')
        this.broadcastCredentialState(result.token)
      }
    }
    this.clearAuthState({ broadcast: false })
  }
}
